import { PureComponent } from 'react';

import PropTypes from 'prop-types';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import dayjs from 'dayjs';

import { buildCalendarDays } from './helpers';
import { dateUtils } from '~/routes/utils';

import styles from './Calendar.scss';

export class Calendar extends PureComponent {
  static propTypes = {
    /** Unique ID for each instance on the Calendar. */
    calendarId: PropTypes.string,
    /** Array of dates, date format is YYYY-MM-DD. */
    dates: PropTypes.arrayOf(PropTypes.string),
    /** Format YYYY-MM-DD */
    selectedDate: PropTypes.string,
    /** Format YYYY-MM, calendar displays this month despite on dates and selectedDate props. */
    selectedMonth: PropTypes.string.isRequired,
    onPreviousClick: PropTypes.func,
    onNextClick: PropTypes.func,
    onDayClick: PropTypes.func,
  };

  static defaultProps = {
    calendarId: nanoid(),
    dates: [],
    selectedDate: undefined,
    onPreviousClick: undefined,
    onNextClick: undefined,
    onDayClick: () => {},
  };

  onDayButtonClick = (date) => {
    const { onDayClick, selectedDate } = this.props;
    if (selectedDate !== date) {
      onDayClick(date);
    }
  };

  render() {
    const { dates, calendarId, selectedDate, selectedMonth, onPreviousClick, onNextClick } =
      this.props;

    const formattedDays = buildCalendarDays(dates, selectedMonth);
    const formattedPreviousMonth = dayjs(selectedMonth).subtract(1, 'month').format('YYYY-MM');
    const formattedNextMonth = dayjs(selectedMonth).add(1, 'month').format('YYYY-MM');

    return (
      <div
        className={classNames(
          styles['c-calendar'],
          selectedDate ? styles['c-calendar__timeSlotsPointer'] : '',
        )}
        data-testid="calendar-container"
      >
        <div className={styles['c-calendar__nav']} data-testid="calendar-navigation">
          {/** PREVIOUS */}
          <div
            className={classNames(
              styles['c-calendar__navButtonContainer'],
              styles['c-calendar__navButtonContainer--previous'],
            )}
          >
            {onPreviousClick ? (
              <button
                onClick={() => onPreviousClick(formattedPreviousMonth)}
                data-testid="calendar-previous-button"
                tabIndex="-1"
              >
                <span className={styles['c-calendar__previousButtonText']}>Earlier dates</span>
              </button>
            ) : null}
          </div>

          {/** TITLE */}
          <h2 className={styles['c-calendar__title']}>
            {dayjs(selectedMonth).format('MMMM YYYY')}
          </h2>

          {/** LATER */}
          <div
            className={classNames(
              styles['c-calendar__navButtonContainer'],
              styles['c-calendar__navButtonContainer--next'],
            )}
          >
            {onNextClick ? (
              <button
                onClick={() => onNextClick(formattedNextMonth)}
                data-testid="calendar-next-button"
                tabIndex="-1"
              >
                <span className={styles['c-calendar__nextButtonText']}>Later dates</span>
              </button>
            ) : null}
          </div>
        </div>

        {/** DATES */}
        <div className={styles['c-calendar__dates']}>
          <ul
            className={styles['c-calendar__datesContainer']}
            data-testid="calendar-grid-container"
          >
            <li className={styles['c-calendar__page']} key={calendarId}>
              <ul className={styles['c-calendar__pageInner']}>
                {dateUtils.DAYS_NAMES.map((dayOfWeek) => (
                  <li
                    className={styles['c-calendar__dayOfWeek']}
                    key={`${calendarId}-${dayOfWeek}`}
                  >
                    {dayOfWeek}
                  </li>
                ))}
                {formattedDays.map((day, i) => {
                  const key = `${day.date}-${i}`;

                  if (day.disabled) {
                    return (
                      <li key={key} className={styles['c-calendar__date']}>
                        <button
                          disabled
                          className={classNames(
                            styles['c-calendar__dateButton'],
                            styles['c-calendar__dateButton--unavailable'],
                          )}
                        >
                          <span>{day.labelDay}</span>
                        </button>
                      </li>
                    );
                  }
                  if (day.hidden) {
                    return <li key={key} className={styles['c-calendar__date']} />;
                  }

                  const dayButtonClassName =
                    selectedDate === day.date
                      ? classNames(
                          styles['c-calendar__dateButton'],
                          styles['c-calendar__dateButton--selected'],
                        )
                      : classNames(
                          styles['c-calendar__dateButton'],
                          styles['c-calendar__dateButton--available'],
                        );

                  return (
                    <li key={key} className={styles['c-calendar__date']}>
                      <button
                        className={dayButtonClassName}
                        onClick={() => this.onDayButtonClick(day.date)}
                      >
                        <span>{day.labelDay}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          </ul>
        </div>
      </div>
    );
  }
}
